import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatRupiah, formatDate, errMsg } from "@/lib/api";
import { Plus, X, Briefcase } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const STATUS = ["Persiapan","Produksi","Load-in","Event","Bongkar","Selesai"];

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [venues, setVenues] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("Semua");
  const [err, setErr] = useState("");
  const { user } = useAuth();
  const canCreate = ["Owner", "Finance Admin", "Sales"].includes(user?.role);
  const [form, setForm] = useState({ name: "", client_id: "", venue_id: "", segment: "Wedding", event_date: "", contract_value: 0, status: "Persiapan" });

  async function load() {
    const [p, c, v] = await Promise.all([api.get("/projects"), api.get("/clients"), api.get("/venues")]);
    setProjects(p.data); setClients(c.data); setVenues(v.data);
  }
  useEffect(() => { load(); }, []);

  async function create(e) {
    e.preventDefault();
    try {
      await api.post("/projects", { ...form, contract_value: Number(form.contract_value) || 0 });
      setOpen(false); setForm({ name: "", client_id: "", venue_id: "", segment: "Wedding", event_date: "", contract_value: 0, status: "Persiapan" });
      await load();
    } catch (ex) { setErr(errMsg(ex)); }
  }

  const filtered = filter === "Semua" ? projects : projects.filter(p => p.status === filter);

  return (
    <div className="p-4 sm:p-8">
      <header className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif-display font-bold">Project Board</h1>
          <p className="text-sm text-[#536263]">Pantau semua project dari persiapan hingga bongkar</p>
          <div className="mt-2 h-px w-12 bg-[#B87333]"></div>
        </div>
        {canCreate && <button onClick={() => setOpen(true)} className="btn-primary" data-testid="project-add-btn"><Plus size={18} />Project Baru</button>}
      </header>

      <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar">
        {["Semua", ...STATUS].map(s => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s.toLowerCase()}`}
            className={`chip whitespace-nowrap ${filter === s ? "bg-[#0F5257] text-white" : "bg-[#F2EEE7] text-[#536263]"}`}>{s}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
        {filtered.map(p => (
          <Link to={`/projects/${p.id}`} key={p.id} className="card-editorial p-5 block" data-testid={`project-card-${p.id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="chip bg-[#F7EFE8] text-[#B87333]">{p.segment}</span>
              <span className="chip bg-[#F2EEE7] text-[#536263]">{p.status}</span>
            </div>
            <h3 className="font-serif-display text-lg font-semibold text-[#1A2627]">{p.name}</h3>
            <p className="text-sm text-[#536263] mt-1">{p.client_name} · {p.venue_name}</p>
            <div className="mt-3 pt-3 border-t border-[#E2DDD3] flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#B87333]">Event</div>
                <div className="text-sm font-medium">{formatDate(p.event_date)}</div>
              </div>
              {p.contract_value !== undefined && <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-[#B87333]">Kontrak</div>
                <div className="font-mono-num text-sm font-semibold text-[#0F5257]">{formatRupiah(p.contract_value)}</div>
              </div>}
            </div>
            {p.balance !== undefined && <div className={`mt-2 text-xs font-mono-num ${p.balance < 0 ? "text-red-700" : "text-[#2D6A4F]"}`}>
              Saldo project: {formatRupiah(p.balance)}
            </div>}
          </Link>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center p-12 text-[#8A999A]"><Briefcase size={32} className="mx-auto mb-2" />Belum ada project</div>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setOpen(false)}>
          <form onSubmit={create} onClick={e => e.stopPropagation()} className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-5 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="project-form">
            <div className="flex items-center justify-between">
              <h3 className="font-serif-display text-xl font-bold">Project Baru</h3>
              <button type="button" onClick={() => setOpen(false)}><X /></button>
            </div>
            <input required placeholder="Nama Project" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full border border-[#E2DDD3] rounded-lg px-3 h-11" />
            <select required value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})} className="w-full border border-[#E2DDD3] rounded-lg px-3 h-11">
              <option value="">Pilih Klien</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={form.venue_id} onChange={e => setForm({...form, venue_id: e.target.value})} className="w-full border border-[#E2DDD3] rounded-lg px-3 h-11">
              <option value="">Pilih Venue</option>
              {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <select value={form.segment} onChange={e => setForm({...form, segment: e.target.value})} className="border border-[#E2DDD3] rounded-lg px-3 h-11">
                {["Wedding","Corporate","Brand Activation","Expo","Lainnya"].map(x => <option key={x}>{x}</option>)}
              </select>
              <input type="date" value={form.event_date?.slice(0,10)} onChange={e => setForm({...form, event_date: e.target.value})} className="border border-[#E2DDD3] rounded-lg px-3 h-11" />
            </div>
            <input type="number" placeholder="Nilai Kontrak (Rp)" value={form.contract_value} onChange={e => setForm({...form, contract_value: e.target.value})} className="w-full border border-[#E2DDD3] rounded-lg px-3 h-11" />
            {err && <div className="text-sm text-red-700">{err}</div>}
            <button type="submit" className="btn-primary w-full" data-testid="project-submit">Simpan</button>
          </form>
        </div>
      )}
    </div>
  );
}
